import type { Permission } from "../types";

export function permissionKey(permission: { resource: string; action: string }): string {
	return `${permission.resource}:${permission.action}`;
}

/**
 * 26 resursning vizual guruhlanishi (spec 2026-09-12-p5 §5.2) —
 * **faqat qatorlarni bo'laklaydi, imkoniyatlar to'plami saqlanadi**.
 * Har guruh nomi backend'ning `resources` massividagi (`accessControl/index.ts`)
 * atamalarga TENG — yangi resurs qo'shilsa va u shu ro'yxatga kiritilmasa,
 * «Boshqa» guruhida ko'rinadi (jimgina yo'qolmaydi).
 */
export const PERMISSION_GROUPS: { titleKey: string; resources: string[] }[] = [
	{ titleKey: "groups.userAccess", resources: ["users", "roles", "role-assignments"] },
	{
		titleKey: "groups.organization",
		resources: ["employees", "departments", "department-grades", "positions"],
	},
	{ titleKey: "groups.deviceConsent", resources: ["devices", "consents"] },
	{ titleKey: "groups.zoneSchedule", resources: ["zones", "schedules"] },
	{ titleKey: "groups.attendanceMonitoring", resources: ["attendance", "suspicions", "sync"] },
	{ titleKey: "groups.fieldActivity", resources: ["tasks", "leads", "lead-forms", "absences"] },
	{ titleKey: "groups.reportsFiles", resources: ["reports", "exports", "files", "notifications"] },
	{ titleKey: "groups.system", resources: ["settings", "audit", "jobs", "retention"] },
];

/**
 * Egasiga ALOHIDA QAROR bilan beriladigan ruxsatlar — sabablari
 * `packages/db/src/seed.ts` izohlarida (tibbiy hujjat, firibgarlik
 * chegaralari, barcha bo'limlar auditi, qaytarib bo'lmaydigan o'chirish).
 * Matritsa ularni belgilaydi va tanlanganda sababini ko'rsatadi.
 */
export const SENSITIVE_PERMISSIONS: ReadonlySet<string> = new Set([
	"absences:read_document",
	"settings:read",
	"audit:read",
	"retention:manage",
	"jobs:retry",
]);

/** ⚠️ Kalitda `:` bo'lmasligi SHART — i18next uni namespace ajratgichi deb o'qiydi. */
export function sensitiveNoteKey(key: string): string {
	return `sensitive.${key.replace(":", "_")}`;
}

/**
 * Kirill (ru/uz) → lotin; qolgan harflar o'zgarishsiz o'tadi. `Map` — obyekt
 * kalitlari ASCII bo'lmagani uchun Biome `useNamingConvention` ni buzardi.
 */
const CYRILLIC = new Map<string, string>([
	["а", "a"],
	["б", "b"],
	["в", "v"],
	["г", "g"],
	["д", "d"],
	["е", "e"],
	["ё", "yo"],
	["ж", "j"],
	["з", "z"],
	["и", "i"],
	["й", "y"],
	["к", "k"],
	["л", "l"],
	["м", "m"],
	["н", "n"],
	["о", "o"],
	["п", "p"],
	["р", "r"],
	["с", "s"],
	["т", "t"],
	["у", "u"],
	["ф", "f"],
	["х", "x"],
	["ц", "ts"],
	["ч", "ch"],
	["ш", "sh"],
	["щ", "sh"],
	["ъ", ""],
	["ы", "i"],
	["ь", ""],
	["э", "e"],
	["ю", "yu"],
	["я", "ya"],
	["ў", "o"],
	["қ", "q"],
	["ғ", "g"],
	["ҳ", "h"],
]);

/**
 * Rol nomidan ID yasaydi — backend `RoleIdSchema` bilan mos:
 * `^[a-z0-9_-]+$`, 1–64 belgi. Apostroflar (`'`, `ʻ`, `ʼ`, `’`, `‘`)
 * TASHLANADI (`Bo'lim` → `bolim`), qolgan har qanday belgi `_` ga aylanadi.
 */
export function slugifyRoleId(name: string): string {
	const latin = [...name.toLowerCase()].map((char) => CYRILLIC.get(char) ?? char).join("");

	return latin
		.replace(/['ʻʼ’‘`]/g, "")
		.replace(/[^a-z0-9_-]+/g, "_")
		.replace(/_+/g, "_")
		.replace(/^[_-]+|[_-]+$/g, "")
		.slice(0, 64)
		.replace(/[_-]+$/, "");
}

function uniqueByKey(list: Permission[]): Permission[] {
	const seen = new Set<string>();
	return list.filter((permission) => {
		const key = permissionKey(permission);
		if (seen.has(key)) {
			return false;
		}
		seen.add(key);
		return true;
	});
}

export function diffPermissions(
	baseline: Permission[],
	current: Permission[]
): { added: Permission[]; removed: Permission[] } {
	const before = new Set(baseline.map(permissionKey));
	const after = new Set(current.map(permissionKey));

	return {
		added: uniqueByKey(current.filter((permission) => !before.has(permissionKey(permission)))),
		removed: uniqueByKey(baseline.filter((permission) => !after.has(permissionKey(permission)))),
	};
}

export function samePermissions(a: Permission[], b: Permission[]): boolean {
	const { added, removed } = diffPermissions(a, b);
	return added.length === 0 && removed.length === 0;
}
